import { Formik } from 'formik';
import { createContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CustomerEditBillingAddressSelect } from './CustomerEditBillingAddressSelect';
import { CustomerEditContactSelect } from './CustomerEditContactSelect';
import { CustomerEditShippingAddressSelect } from './CustomerEditShippingAddressSelect';

import { Box } from '@components/Box';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import {
  CancelButton,
  FormikCheckbox,
  FormikTextArea,
  SubmitButton,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { Loading } from '@components/Loading';
import { Space } from '@components/Space';
import { useNotification } from '@hooks/useNotification';
import {
  customerValidation,
  CustomerViewModel,
  customerDefaultValue,
} from '@models/Customer';
import { CustomerService } from '@services/Customer';
import { routes } from 'src/config/routes';

interface ICustomerEditContextModel {
  customer: CustomerViewModel;
  setCustomer: React.Dispatch<React.SetStateAction<CustomerViewModel>>;
}

export const CustomerEditContext = createContext<ICustomerEditContextModel>({
  customer: customerDefaultValue,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCustomer: () => {},
});

export const CustomerEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocation = location.state?.from || routes.RNC_CUSTOMERS;
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [customer, setCustomer] =
    useState<CustomerViewModel>(customerDefaultValue);
  const [submitType, setSubmitType] = useState<'saveAndClose' | 'save'>(
    'saveAndClose'
  );

  const { showSuccess, showError } = useNotification();

  const loadData = async () => {
    const result = await CustomerService.getById(Number(id));

    if (result.isSuccess && 'data' in result) {
      setCustomer(result.data);
      setIsLoading(false);
      console.log('Success: Fetched the record.');
    } else {
      console.error('Error: Failed to get record.', result.errorMessages);
      showError(`${result.errorMessages}`);
    }
  };

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, []);

  if (isLoading) {
    return (
      <>
        <Loading />
      </>
    );
  } else {
    return (
      <>
        <CustomerEditContext.Provider value={{ customer, setCustomer }}>
          <Box>
            <div className="pt-2 lg:pb-6 md:pb-4 pb-2">
              <p className="w-full text-center lg:text-2xl md:text-lg text-md font-bold text-primary-900">
                Edit Customer Details
              </p>
            </div>

            <Formik
              initialValues={customer}
              validationSchema={customerValidation}
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={async (values, actions) => {
                values.primaryContact = undefined;
                values.defaultBillingAddress = undefined;
                values.defaultShippingAddress = undefined;
                values.billingAddresses = undefined;
                values.shippingAddresses = undefined;

                values.contactID = customer.contactID;
                values.billingID = customer.billingID;
                values.shippingID = customer.shippingID;

                try {
                  CustomerService.update(Number(id), values)
                    .then((result) => {
                      if (result.isSuccess) {
                        showSuccess('Customer updated successfully.');

                        if (submitType === 'saveAndClose') {
                          navigate(previousLocation);
                        } else if (submitType === 'save') {
                          window.location.reload();
                        }
                      } else {
                        console.error(
                          'Error: Failed to update record.',
                          result.errorMessages
                        );
                        showError(`${result.errorMessages}`);
                      }
                    })
                    .finally(() => {
                      actions.setSubmitting(false);
                    });
                } catch (error) {
                  console.error(error);
                  actions.setSubmitting(false);
                }
              }}
            >
              {(formikProps) => {
                return (
                  <>
                    <form method="POST" onSubmit={formikProps.handleSubmit}>
                      <FormSectionContainer>
                        <FormikInput
                          label="Name / Company Name"
                          name="companyName"
                        />
                        <FormikInput
                          label="Account Number"
                          name="accountNumber"
                        />
                        <FormikInput label="Website" name="website" />

                        <CustomerEditContactSelect />

                        <CustomerEditBillingAddressSelect />

                        <FormikCheckbox
                          label="Same as Billing Address"
                          name="isSameShipping"
                        />

                        {!formikProps.values.isSameShipping && (
                          <CustomerEditShippingAddressSelect />
                        )}

                        <FormikTextArea label="Notes" name="notes" />

                        <FormikCheckbox label="Tax Exempt" name="isTaxExempt" />

                        <Space />
                      </FormSectionContainer>
                      <FormButtonsContainer>
                        <CancelButton
                          onClick={() => {
                            navigate(previousLocation);
                          }}
                        />

                        <SubmitButton
                          label="Save & Close"
                          disabled={
                            formikProps.isSubmitting || !formikProps.isValid
                          }
                          onClick={() => setSubmitType('saveAndClose')}
                        />

                        <SubmitButton
                          label="Save"
                          disabled={
                            formikProps.isSubmitting || !formikProps.isValid
                          }
                          onClick={() => setSubmitType('save')}
                        />
                      </FormButtonsContainer>
                    </form>
                  </>
                );
              }}
            </Formik>
          </Box>
        </CustomerEditContext.Provider>
      </>
    );
  }
};
